/**
 * Mobile / tablet navigation - panel stack controller.
 *
 * Markup lives in snippets/nav-drawer.liquid. Every parent link is a button
 * that pushes a sibling panel; there are no dropdowns to expand. The drawer
 * itself (open/close, scroll lock, focus trap) is still owned by the theme's
 * Drawer class - this only manages what happens inside it.
 *
 * All click/keyboard handling is delegated to `document` and every bit of
 * state is derived from the DOM (the `is-active` class plus each panel's
 * data-mnav-parent chain). That way the menu keeps working even after the
 * theme editor or the CLI's hot reload replaces the section's HTML - freshly
 * injected markup needs no rebinding.
 */
(function () {
  'use strict';

  var SELECTORS = {
    root: '[data-mobile-nav]',
    panels: '[data-mnav-panels]',
    panel: '[data-mnav-panel]',
    rootPanel: '[data-mnav-root]',
    open: '[data-mnav-open]',
    back: '[data-mnav-back]',
    title: '[data-mnav-title-text]',
  };

  var CLASSES = {
    active: 'is-active',
    behind: 'is-behind',
    peeking: 'is-peeking',
    revealing: 'is-revealing',
    scrolled: 'is-scrolled',
    dragging: 'mnav--dragging',
    noAnim: 'mnav--no-anim',
    searching: 'mnav--searching',
    drawerOpen: 'is-open',
    titleIn: 'is-in',
  };

  // Matches --mnav-dur in component-mobile-nav.css.
  var DURATION = 420;
  var SUPPORTS_INERT = 'inert' in HTMLElement.prototype;
  var revealTimers = typeof WeakMap === 'function' ? new WeakMap() : null;

  /* ================ DOM-derived state ================ */

  function getPanels(root) {
    return Array.prototype.slice.call(root.querySelectorAll(SELECTORS.panel));
  }

  function getActive(root) {
    return root.querySelector(SELECTORS.panel + '.' + CLASSES.active) || root.querySelector(SELECTORS.rootPanel);
  }

  function getParent(root, panel) {
    if (!panel) return null;
    var id = panel.getAttribute('data-mnav-parent');
    if (!id) return null;
    var parent = document.getElementById(id);
    return parent && root.contains(parent) ? parent : null;
  }

  // Chain from the root panel down to `panel`, both included.
  function getChain(root, panel) {
    var chain = [];
    var current = panel;
    var guard = 0;

    while (current && guard < 8) {
      chain.unshift(current);
      current = getParent(root, current);
      guard += 1;
    }

    return chain;
  }

  function triggerFor(root, panel) {
    return root.querySelector('[data-mnav-open="' + panel.id + '"]');
  }

  /* ================ Rendering ================ */

  function render(root, active) {
    var behind = getParent(root, active);
    var chain = getChain(root, active);
    var depth = chain.length - 1;

    getPanels(root).forEach(function (panel) {
      var isActive = panel === active;

      panel.classList.toggle(CLASSES.active, isActive);
      panel.classList.toggle(CLASSES.behind, panel === behind);
      if (!isActive) panel.classList.remove(CLASSES.peeking, CLASSES.revealing);

      if (isActive) {
        panel.removeAttribute('aria-hidden');
      } else {
        panel.setAttribute('aria-hidden', 'true');
      }
      if (SUPPORTS_INERT) panel.inert = !isActive;
    });

    Array.prototype.forEach.call(root.querySelectorAll(SELECTORS.open), function (button) {
      var target = document.getElementById(button.getAttribute('data-mnav-open'));
      button.setAttribute('aria-expanded', target && chain.indexOf(target) > -1 ? 'true' : 'false');
    });

    root.setAttribute('data-mnav-depth', String(depth));
    root.classList.toggle(CLASSES.scrolled, active.scrollTop > 4);

    var back = root.querySelector(SELECTORS.back);
    if (back) {
      back.disabled = depth === 0;
      var backLabel = back.getAttribute('data-mnav-back-label') || 'Back';
      var target = behind ? behind.getAttribute('data-mnav-title') : null;
      back.setAttribute('aria-label', backLabel + (target ? ': ' + target : ''));
    }

    var title = root.querySelector(SELECTORS.title);
    if (title && depth > 0) {
      setTitle(title, active.getAttribute('data-mnav-title') || '');
    }
  }

  function setTitle(el, text) {
    if (el.textContent === text) return;

    el.textContent = text;
    el.classList.remove(CLASSES.titleIn);
    void el.offsetWidth;
    el.classList.add(CLASSES.titleIn);
  }

  function reveal(root, panel) {
    if (!panel) return;

    if (revealTimers) {
      clearTimeout(revealTimers.get(root));
    }
    panel.classList.remove(CLASSES.revealing);
    void panel.offsetWidth;
    panel.classList.add(CLASSES.revealing);

    var timer = setTimeout(function () {
      panel.classList.remove(CLASSES.revealing);
    }, 1600);
    if (revealTimers) revealTimers.set(root, timer);
  }

  /* ================ Navigation ================ */

  function push(root, panel) {
    if (!panel || panel === getActive(root)) return;

    panel.scrollTop = 0;
    render(root, panel);
    reveal(root, panel);

    if (panel.focus) panel.focus({preventScroll: true});
  }

  function pop(root) {
    var leaving = getActive(root);
    var target = getParent(root, leaving);
    if (!target) return false;

    render(root, target);

    var trigger = triggerFor(root, leaving);
    if (trigger && trigger.focus) trigger.focus({preventScroll: true});
    return true;
  }

  function reset(root) {
    exitSearch(root);

    var rootPanel = root.querySelector(SELECTORS.rootPanel);
    if (!rootPanel || getActive(root) === rootPanel) return;

    root.classList.add(CLASSES.noAnim);
    render(root, rootPanel);
    rootPanel.scrollTop = 0;

    // Flush the class change before transitions come back on.
    void root.offsetHeight;
    requestAnimationFrame(function () {
      root.classList.remove(CLASSES.noAnim);
    });
  }

  /* ================ Inline search ================
     Typing in the drawer's search field swaps the menu content for live
     predictive results rendered inside the same panel: suggestions, a product
     grid, collection rows and a view-all link, all fetched from Shopify's
     /search/suggest.json. Clearing the field brings the menu back. */

  var SEARCH_DEBOUNCE = 220;
  var searchTimer = null;
  var searchController = null;

  function searchParts(root) {
    return {
      input: root.querySelector('[data-mnav-search-input]'),
      results: root.querySelector('[data-mnav-search-results]'),
      clear: root.querySelector('[data-mnav-search-clear]'),
    };
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  // Renders a suggest.json price ("49.99") through the shop's money format.
  // The theme's currency helper is not exposed on window, so the four standard
  // Shopify amount placeholders are implemented here directly.
  function formatMoney(value) {
    var amount = parseFloat(value);
    if (isNaN(amount)) return '';

    var format = (window.theme && window.theme.moneyFormat) || '€{{ amount }}';
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
    var key = match ? match[1] : 'amount';
    var fixed = amount.toFixed(2);
    var whole = fixed.split('.')[0];
    var decimals = fixed.split('.')[1];
    var text;

    var group = function (digits, separator) {
      return digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    };

    switch (key) {
      case 'amount_no_decimals':
        text = group(String(Math.round(amount)), ',');
        break;
      case 'amount_with_comma_separator':
        text = group(whole, '.') + ',' + decimals;
        break;
      case 'amount_no_decimals_with_comma_separator':
        text = group(String(Math.round(amount)), '.');
        break;
      default:
        text = group(whole, ',') + '.' + decimals;
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, text).replace(/<[^>]+>/g, '').trim();
  }

  function sizedImage(url, width) {
    if (!url) return null;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function searchPageUrl(input, query) {
    var base = input.getAttribute('data-search-url') || '/search';
    return base + '?q=' + encodeURIComponent(query) + '&options%5Bprefix%5D=last';
  }

  function setLoading(root, on) {
    var loader = root.querySelector('[data-mnav-search-loader]');
    if (loader) loader.classList.toggle('is-loading', on);

    var parts = searchParts(root);
    if (parts.results) parts.results.classList.toggle('is-loading', on);
  }

  // Search mode is explicit: entered on focus or via the header search icon,
  // left via the clear button (when empty), Escape, or a drawer reset.
  function enterSearch(root, opts) {
    opts = opts || {};
    ensureBindings(root);

    var parts = searchParts(root);
    var wasSearching = root.classList.contains(CLASSES.searching);

    root.classList.add(CLASSES.searching);
    if (parts.clear) parts.clear.hidden = false;

    if (!wasSearching && parts.input && !parts.input.value.trim()) {
      renderHint(root);
    }
    if (opts.focus && parts.input) parts.input.focus({preventScroll: true});
  }

  function exitSearch(root) {
    var parts = searchParts(root);

    clearTimeout(searchTimer);
    if (searchController) searchController.abort();
    setLoading(root, false);

    root.classList.remove(CLASSES.searching);
    if (parts.clear) parts.clear.hidden = true;

    if (parts.input) {
      parts.input.value = '';
      if (parts.input === document.activeElement) parts.input.blur();
    }

    if (parts.results) {
      parts.results.hidden = true;
      parts.results.innerHTML = '';
    }
  }

  function renderHint(root) {
    var parts = searchParts(root);
    if (!parts.results) return;

    parts.results.innerHTML = '';
    parts.results.hidden = false;
    parts.results.appendChild(
      el('p', 'mnav__results-hint mnav__results-section', parts.results.getAttribute('data-label-start') || '')
    );
  }

  function onQuery(root) {
    var parts = searchParts(root);
    if (!parts.input || !parts.results) return;

    var query = parts.input.value.trim();
    enterSearch(root);

    clearTimeout(searchTimer);
    if (searchController) searchController.abort();
    setLoading(root, false);

    if (!query) {
      renderHint(root);
      return;
    }

    searchTimer = setTimeout(function () {
      fetchResults(root, query);
    }, SEARCH_DEBOUNCE);
  }

  function fetchResults(root, query) {
    var parts = searchParts(root);
    var base = parts.input.getAttribute('data-predictive-url') || '/search/suggest';
    var params =
      'q=' +
      encodeURIComponent(query) +
      '&resources%5Btype%5D=product,collection,query' +
      '&resources%5Blimit%5D=8' +
      '&resources%5Boptions%5D%5Bunavailable_products%5D=last' +
      '&resources%5Boptions%5D%5Bprefix%5D=last';

    searchController = window.AbortController ? new AbortController() : null;
    setLoading(root, true);
    parts.results.hidden = false;

    fetch(base + '.json?' + params, {
      signal: searchController ? searchController.signal : undefined,
      headers: {Accept: 'application/json'},
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        // Ignore stale responses: only render for what is in the field now.
        if (parts.input.value.trim() !== query) return;
        setLoading(root, false);
        renderResults(root, query, (data.resources && data.resources.results) || {});
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        setLoading(root, false);
        renderResults(root, query, {});
      });
  }

  /* Mirrors the badge stack from snippets/product-grid-item.liquid, driven by
     the same theme settings (passed in via data-badge-config). */
  function buildBadges(product, config, onSale, price, compare) {
    var items = [];

    if (config.sold && product.available === false) {
      items.push({cls: 'product-badge__item--sold', text: config.soldText || 'Sold out'});
    }

    var tagBadges = config.tagBadges || [];
    if (tagBadges.length) {
      var productTags = (product.tags || []).map(function (tag) {
        return String(tag).toLowerCase();
      });
      for (var b = 0; b < tagBadges.length; b++) {
        if (productTags.indexOf(tagBadges[b].tag) !== -1) {
          items.push({
            cls: 'product-badge__item--image',
            img: tagBadges[b].image,
            width: tagBadges[b].width,
            widthMobile: tagBadges[b].widthMobile
          });
          break;
        }
      }
    }

    if (onSale && config.saving) {
      var text = null;
      if (config.savingType === 'percentage') {
        var percent = Math.round((((compare - price) / compare) * 100) / 5) * 5;
        if (percent > 0) text = '-' + percent + '%';
      } else {
        text = formatMoney(compare - price).replace(/[,.]00$/, '');
      }
      if (text) items.push({cls: 'product-badge__item--saving', text: text});
    } else if (onSale && config.sale && !config.saving) {
      items.push({cls: 'product-badge__item--sale', text: config.saleText || 'Sale'});
    }

    if (!items.length) return null;

    var badge = el('div', 'product-badge');
    items.forEach(function (item) {
      var badgeItem = el('div', 'product-badge__item ' + item.cls);
      if (item.img) {
        if (item.width) badgeItem.style.setProperty('--tag-badge-width', item.width + 'px');
        if (item.widthMobile) badgeItem.style.setProperty('--tag-badge-width-mobile', item.widthMobile + 'px');
        var img = document.createElement('img');
        img.src = item.img;
        img.alt = '';
        img.loading = 'lazy';
        badgeItem.appendChild(img);
      } else {
        badgeItem.appendChild(el('span', null, item.text));
      }
      badge.appendChild(badgeItem);
    });
    return badge;
  }

  function renderResults(root, query, results) {
    var parts = searchParts(root);
    var box = parts.results;
    var products = results.products || [];
    var collections = results.collections || [];
    var queries = results.queries || [];
    var sectionIndex = 0;

    box.innerHTML = '';
    box.classList.remove('is-loading');
    box.hidden = false;

    function section(labelKey) {
      var wrap = el('div', 'mnav__results-section');
      wrap.style.setProperty('--i', String(sectionIndex));
      sectionIndex += 1;
      var label = box.getAttribute('data-label-' + labelKey);
      if (label) {
        // A div dodges the theme's heavy h3 sizing; semantics via ARIA.
        var heading = el('div', 'mnav__results-label', label);
        heading.setAttribute('role', 'heading');
        heading.setAttribute('aria-level', '3');
        wrap.appendChild(heading);
      }
      box.appendChild(wrap);
      return wrap;
    }

    if (queries.length) {
      var suggestWrap = section('suggestions');
      var suggestList = el('ul', 'mnav__list');
      queries.slice(0, 3).forEach(function (item) {
        var li = el('li', 'mnav__item');
        var button = el('button', 'mnav__row mnav__row--small', item.text);
        button.type = 'button';
        button.setAttribute('data-mnav-suggest', item.text);
        li.appendChild(button);
        suggestList.appendChild(li);
      });
      suggestWrap.appendChild(suggestList);
    }

    if (products.length) {
      var badgeConfig;
      try {
        badgeConfig = JSON.parse(box.getAttribute('data-badge-config') || '{}');
      } catch (parseError) {
        badgeConfig = {};
      }

      var productWrap = section('products');
      var grid = el('div', 'mnav__results-grid');
      products.slice(0, 6).forEach(function (product) {
        var price = parseFloat(product.price_min || product.price);
        var compare = parseFloat(product.compare_at_price_min);
        if (!(compare > price)) compare = parseFloat(product.compare_at_price_max);
        var onSale = compare > price;

        var card = el('a', 'mnav__result-product');
        card.href = product.url;

        var media = el('span', 'mnav__result-product__media');
        var imageUrl = product.featured_image && product.featured_image.url;
        if (imageUrl) {
          var img = document.createElement('img');
          img.src = sizedImage(imageUrl, 400);
          img.alt = (product.featured_image && product.featured_image.alt) || product.title;
          img.loading = 'lazy';
          media.appendChild(img);
        }
        var badges = buildBadges(product, badgeConfig, onSale, price, compare);
        if (badges) media.appendChild(badges);
        card.appendChild(media);

        card.appendChild(el('span', 'mnav__result-product__title', product.title));

        var priceWrap = el('span', 'mnav__result-product__price');
        if (onSale) {
          priceWrap.appendChild(el('span', 'mnav__result-product__price-new', formatMoney(product.price)));
          var compareEl = document.createElement('s');
          compareEl.textContent = formatMoney(compare);
          priceWrap.appendChild(compareEl);
        } else {
          priceWrap.textContent = formatMoney(product.price);
        }
        card.appendChild(priceWrap);

        grid.appendChild(card);
      });
      productWrap.appendChild(grid);
    }

    if (collections.length) {
      var collectionWrap = section('collections');
      var collectionList = el('ul', 'mnav__list');
      collections.slice(0, 4).forEach(function (collection) {
        var li = el('li', 'mnav__item');
        var row = el('a', 'mnav__row mnav__row--sub');
        row.href = collection.url;
        row.appendChild(el('span', 'mnav__label', collection.title));
        var chevron = el('span', 'mnav__chevron');
        chevron.innerHTML =
          '<svg aria-hidden="true" focusable="false" role="presentation" class="icon" viewBox="0 0 192 192"><path d="m72 36 60 60-60 60" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        row.appendChild(chevron);
        li.appendChild(row);
        collectionList.appendChild(li);
      });
      collectionWrap.appendChild(collectionList);
    }

    if (!products.length && !collections.length && !queries.length) {
      var emptyLabel = (box.getAttribute('data-label-empty') || '').replace('__TERMS__', query);
      box.appendChild(el('p', 'mnav__results-empty mnav__results-section', emptyLabel));
    } else {
      var viewAllWrap = el('div', 'mnav__results-section');
      viewAllWrap.style.setProperty('--i', String(sectionIndex));
      var viewAll = el('a', 'mnav__viewall mnav__results-viewall');
      viewAll.href = searchPageUrl(parts.input, query);
      viewAll.appendChild(el('span', null, box.getAttribute('data-label-view-all') || 'View all'));
      viewAll.insertAdjacentHTML(
        'beforeend',
        '<svg aria-hidden="true" focusable="false" role="presentation" class="icon" viewBox="0 0 192 192"><path d="M30 96h132M108 42l54 54-54 54" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      );
      viewAllWrap.appendChild(viewAll);
      box.appendChild(viewAllWrap);
    }
  }

  /* ================ Delegated events ================ */

  // Set while the header search icon opens the drawer, so the drawer-open
  // reset does not wipe the search mode that was just entered.
  var headerSearchPending = false;

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    // Header search icon: the button doubles as a theme drawer toggle (that
    // part is the theme's job), this layers search mode + focus on top,
    // synchronously so mobile keyboards come up.
    var headerSearch = event.target.closest('[data-mnav-header-search]');
    if (headerSearch) {
      var drawer = document.querySelector(SELECTORS.root);
      if (!drawer) return;
      var drawerParts = searchParts(drawer);

      headerSearchPending = true;
      enterSearch(drawer, {focus: true});

      // The theme's focus trap grabs focus when the slide-in ends; take it back.
      var refocus = function (transitionEvent) {
        if (transitionEvent.target !== drawer) return;
        drawer.removeEventListener('transitionend', refocus);
        if (drawer.classList.contains(CLASSES.searching) && drawerParts.input) {
          requestAnimationFrame(function () {
            drawerParts.input.focus({preventScroll: true});
          });
        }
      };
      drawer.addEventListener('transitionend', refocus);
      return;
    }

    var openButton = event.target.closest(SELECTORS.open);
    if (openButton) {
      var root = openButton.closest(SELECTORS.root);
      var panel = document.getElementById(openButton.getAttribute('data-mnav-open'));

      if (root && panel && root.contains(panel)) {
        event.preventDefault();
        ensureBindings(root);
        push(root, panel);
      }
      return;
    }

    var backButton = event.target.closest(SELECTORS.back);
    if (backButton) {
      var backRoot = backButton.closest(SELECTORS.root);
      if (backRoot) {
        event.preventDefault();
        pop(backRoot);
      }
      return;
    }

    // Clear behaves in two steps: with text it wipes the query and keeps the
    // keyboard, when already empty it exits back to the menu.
    var clearButton = event.target.closest('[data-mnav-search-clear]');
    if (clearButton) {
      var clearRoot = clearButton.closest(SELECTORS.root);
      if (clearRoot) {
        event.preventDefault();
        var clearParts = searchParts(clearRoot);
        if (clearParts.input && clearParts.input.value.trim()) {
          clearParts.input.value = '';
          onQuery(clearRoot);
          clearParts.input.focus({preventScroll: true});
        } else {
          exitSearch(clearRoot);
        }
      }
      return;
    }

    var suggestButton = event.target.closest('[data-mnav-suggest]');
    if (suggestButton) {
      var suggestRoot = suggestButton.closest(SELECTORS.root);
      if (suggestRoot) {
        event.preventDefault();
        var suggestParts = searchParts(suggestRoot);
        if (suggestParts.input) {
          suggestParts.input.value = suggestButton.getAttribute('data-mnav-suggest');
          onQuery(suggestRoot);
        }
      }
    }
  });

  document.addEventListener('input', function (event) {
    if (!event.target || !event.target.matches || !event.target.matches('[data-mnav-search-input]')) return;

    var root = event.target.closest(SELECTORS.root);
    if (root) onQuery(root);
  });

  // Focusing the field is enough to step into search mode (hint state).
  document.addEventListener('focusin', function (event) {
    if (!event.target || !event.target.matches || !event.target.matches('[data-mnav-search-input]')) return;

    var root = event.target.closest(SELECTORS.root);
    if (root) enterSearch(root);
  });

  // Enter submits to the full search results page.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    if (!event.target || !event.target.matches || !event.target.matches('[data-mnav-search-input]')) return;

    var query = event.target.value.trim();
    if (!query) return;

    event.preventDefault();
    window.location.assign(searchPageUrl(event.target, query));
  });

  // Capture phase so Escape pops one level before the theme's own keyup
  // handler on the drawer closes the whole thing.
  document.addEventListener(
    'keyup',
    function (event) {
      if (event.code !== 'Escape' && event.key !== 'Escape') return;

      var root = event.target && event.target.closest ? event.target.closest(SELECTORS.root) : null;
      if (!root || !root.classList.contains(CLASSES.drawerOpen)) return;

      // First Escape exits an active search, the next one pops a panel, and
      // only at the root does the theme get to close the drawer.
      if (root.classList.contains(CLASSES.searching)) {
        event.stopPropagation();
        exitSearch(root);
        return;
      }

      if (!getParent(root, getActive(root))) return;

      event.stopPropagation();
      pop(root);
    },
    true
  );

  // The theme's Drawer dispatches these on document around open/close.
  document.addEventListener('theme:drawer:open', function () {
    // is-open lands on the drawer right after this event fires.
    setTimeout(function () {
      var root = document.querySelector(SELECTORS.root + '.' + CLASSES.drawerOpen);
      if (!root) return;

      ensureBindings(root);

      // Opened via the header search icon: keep the search mode just entered.
      if (headerSearchPending) {
        headerSearchPending = false;
        return;
      }

      reset(root);
      reveal(root, getActive(root));
    }, 30);
  });

  document.addEventListener('theme:drawer:close', function () {
    headerSearchPending = false;
    Array.prototype.forEach.call(document.querySelectorAll(SELECTORS.root), function (root) {
      reset(root);
    });
  });

  /* ================ Per-element listeners ================
     Scroll and touch need real listeners on the panels container (scroll does
     not bubble; touchmove must be non-passive). Bound lazily and re-bound
     whenever the node is replaced - the marker property dies with the node. */

  function ensureBindings(root) {
    var panels = root.querySelector(SELECTORS.panels);
    if (!panels || panels.__mnavBound) return;
    panels.__mnavBound = true;

    render(root, getActive(root));
    bindScrollShadow(root, panels);
    bindSwipeBack(root, panels);
  }

  function bindScrollShadow(root, panels) {
    panels.addEventListener(
      'scroll',
      function (event) {
        if (event.target !== getActive(root)) return;
        root.classList.toggle(CLASSES.scrolled, event.target.scrollTop > 4);
      },
      true
    );
  }

  /**
   * Drag-to-go-back. The active panel follows the finger and the panel behind
   * it slides back into place proportionally; releasing past a third of the
   * width (or with a quick flick) completes the pop, otherwise it springs back.
   */
  function bindSwipeBack(root, panels) {
    var startX = 0;
    var startY = 0;
    var startTime = 0;
    var width = 1;
    var delta = 0;
    var decided = false;
    var dragging = false;
    var front = null;
    var back = null;

    panels.addEventListener(
      'touchstart',
      function (event) {
        if (event.touches.length !== 1) return;

        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        startTime = Date.now();
        width = panels.offsetWidth || 1;
        delta = 0;
        decided = false;
        dragging = false;
      },
      {passive: true}
    );

    panels.addEventListener(
      'touchmove',
      function (event) {
        if (event.touches.length !== 1) return;

        var dx = event.touches[0].clientX - startX;
        var dy = event.touches[0].clientY - startY;

        if (!decided) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;

          decided = true;
          front = getActive(root);
          back = getParent(root, front);
          dragging = !!back && dx > 0 && Math.abs(dx) > Math.abs(dy) * 1.4;
          if (!dragging) return;

          root.classList.add(CLASSES.dragging);
          back.classList.add(CLASSES.peeking);
        }

        if (!dragging) return;

        event.preventDefault();
        delta = Math.max(0, Math.min(dx, width));

        var progress = delta / width;
        front.style.transform = 'translate3d(' + delta + 'px, 0, 0)';
        back.style.transform = 'translate3d(' + (-26 + 26 * progress) + '%, 0, 0)';
        back.style.opacity = String(progress);
      },
      {passive: false}
    );

    var release = function () {
      if (!dragging) {
        decided = false;
        return;
      }

      var progress = delta / width;
      var flicked = Date.now() - startTime < 320 && delta > 48;
      var settled = back;

      dragging = false;
      decided = false;

      front.style.transform = '';
      back.style.transform = '';
      back.style.opacity = '';
      root.classList.remove(CLASSES.dragging);

      if (progress > 0.34 || flicked) {
        pop(root);
      } else {
        // Let the spring-back animation finish before the panel is hidden again.
        setTimeout(function () {
          settled.classList.remove(CLASSES.peeking);
        }, DURATION + 40);
      }
    };

    panels.addEventListener('touchend', release);
    panels.addEventListener('touchcancel', release);
  }

  /* ================ Init ================ */

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll(SELECTORS.root), function (root) {
      ensureBindings(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
